import { config } from '../config';
import {
  type Deployment,
  type GithubDeploymentAuthRequest,
  type GithubDeploymentAuthResult,
  type InfrastructureApp,
  type PlanResult,
  type VerificationResult,
} from '../types.ts';

/**
 * API client for deployment dashboard
 */
class ApiClient {
  private readonly baseUrl: string;
  private getAccessToken: (() => Promise<string>) | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Set the function to get access token
   */
  setAccessTokenProvider(provider: () => Promise<string>) {
    this.getAccessToken = provider;
  }

  /**
   * Make an authenticated request to the API
   */
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    if (!this.getAccessToken) {
      throw new Error('Access token provider not set');
    }

    const token = await this.getAccessToken();
    const url = `${this.baseUrl}/${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options?.headers,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      let message = `API request failed: ${response.statusText}`;
      try {
        const payload = (await response.json()) as { error?: string };
        if (payload.error) {
          message = payload.error;
        }
      } catch {
        // Keep the generic response status when the API does not return JSON.
      }

      throw new Error(message);
    }

    return response.json();
  }

  /**
   * Get all infrastructure apps
   */
  async getApps(): Promise<InfrastructureApp[]> {
    return this.request<InfrastructureApp[]>('apps');
  }

  /**
   * Get all deployments
   */
  async getDeployments(): Promise<Deployment[]> {
    return this.request<Deployment[]>('deployments');
  }

  /**
   * Get all plan results
   */
  async getPlanResults(): Promise<PlanResult[]> {
    return this.request<PlanResult[]>('plans');
  }

  /**
   * Get all verification results
   */
  async getVerificationResults(): Promise<VerificationResult[]> {
    return this.request<VerificationResult[]>('verifications');
  }

  /**
   * Set verification status for a deployment
   */
  async setVerificationStatus(deploymentId: number, status: string): Promise<void> {
    await this.request('verifications', {
      method: 'POST',
      body: JSON.stringify({ deploymentId, status }),
    });
  }

  /**
   * Update app configuration
   */
  async updateApp(
    appName: string,
    environments: string[],
    validationInstructions?: Record<string, { info?: string }>,
    bulkDeploymentExclusions?: string[],
  ): Promise<void> {
    await this.request('apps', {
      method: 'PATCH',
      body: JSON.stringify({
        name: appName,
        environments: environments.join(','),
        validationInstructions,
        bulkDeploymentExclusions,
      }),
    });
  }

  async dispatchGithubDeployments(
    request: GithubDeploymentAuthRequest,
  ): Promise<GithubDeploymentAuthResult> {
    return this.request<GithubDeploymentAuthResult>('github/deployments/dispatch', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }
}

export const apiClient = new ApiClient(config.api.baseUrl);
